<template>
  <atlas-image
    ref="atlasImageRef"
    :src="imageSrc"
    :alt="alt ? alt : caption"
    :caption="caption"
    :credit="credit"
    loading="lazy"
    :class="imageClasses"
  />
</template>
<script setup lang="ts">
import { AtlasImage } from '@nhst/ui';
import debounce from 'lodash.debounce';
import type { PropType } from 'vue';
import type { PointOfInterest } from '~/types/elements';
import type { PublicationContext } from '~/types';

const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

const props = defineProps({
  src: {
    type: String,
    required: false,
    default: null
  },
  alt: {
    type: String,
    required: false,
    default: null
  },
  caption: {
    type: String,
    required: false,
    default: null
  },
  credit: {
    type: String,
    required: false,
    default: null
  },
  pointOfInterest: {
    type: Object as PropType<PointOfInterest>,
    required: false,
    default: () => ({})
  },
  imageCrops: {
    type: Object,
    required: false,
    default: () => ({})
  },
  ratio: {
    type: String,
    required: false,
    default: '3x2'
  },
  layout: {
    type: String,
    required: false,
    default: 'default'
  },
  variant: {
    type: String,
    required: false,
    default: null
  },
  matchAreaOfInterest: {
    type: Boolean,
    required: false,
    default: false
  },
  originalWidth: {
    type: Number,
    required: false,
    default: 980
  },
  originalHeight: {
    type: Number,
    required: false,
    default: 0
  }
});

const fp = computed(() =>
  props.pointOfInterest?.centerX && props.pointOfInterest?.centerY
    ? [props.pointOfInterest.centerX, props.pointOfInterest.centerY]
    : []
);

const atlasImageRef = ref<any>(null);
const figureEl = ref<HTMLElement | null>(null);

const imageLib = useImage(props.src, {
  fp: fp.value,
  poiData: props.pointOfInterest,
  imageCrops: props.imageCrops,
  ratio: props.ratio || '3x2',
  isMatchAoi: props.matchAreaOfInterest,
  originalWidth: props.originalWidth,
  originalHeight: props.originalHeight
});

const imgWidth = ref(props.originalWidth);
const imgHeight = ref(imageLib.getHeightByRatio(imgWidth.value));

const imageSrc = computed(() =>
  imageLib.getImageSrc(rtConfPub?.imageFormat || 'auto', imgWidth.value, imgHeight.value) || props.src
);

const imageClasses = computed(() =>
  [props.ratio ? `ratio-${props.ratio}` : null, props.layout, props.variant].filter(Boolean)
);

function handleResize () {
  const pixelRatio = window.devicePixelRatio || 1;
  const root = atlasImageRef.value?.pictureRef?.parentElement ??
    atlasImageRef.value?.imgRef?.closest('.atlas-image') ??
    null;
  figureEl.value = root;
  const measuredWidth = root?.offsetWidth || root?.clientWidth;
  if (!measuredWidth) {
    return;
  }
  imgWidth.value = measuredWidth * pixelRatio;
  imgHeight.value = imageLib.getHeightByRatio(imgWidth.value);
}

const debouncedHandleResize = debounce(handleResize, 500);

onMounted(() => {
  if ('ResizeObserver' in window) {
    useResizeObserver(figureEl, debouncedHandleResize);
  } else {
    useEventListener('resize', debouncedHandleResize);
  }

  nextTick(() => {
    requestAnimationFrame(handleResize);
  });
});
</script>

<style lang="scss">
.atlas-image {
  margin-left: auto;
  margin-right: auto;

  --atlas-image_figcaption_font-family: var(--typography-utility-m-font-family);
  --atlas-image_max-width: var(--content-width);
  --atlas-image_border-radius: var(--border-radius-default-min);

  @media only screen and (width >= 980px) {
    --atlas-image_border-radius: var(--border-radius-default-max);

  }

  &.small {
    max-width: var(--content-width);
  }

  &.center {
    --atlas-image_max-width: var(--grid-width);
  }

  &.center,
  &.default,
  &.medium {
    inline-size: 100%;

    @media only screen and (width >= 690px) {
      inline-size: min(880px, 100vw - calc(var(--site-padding) * 2));
    }

    --atlas-image_figcaption_padding: 3px var(--site-padding);

    @media only screen and (width >= 690px) {
      --atlas-image_figcaption_padding: 3px 0px;
    }
  }

  &.full {
    inline-size: min(100%, 100vw);
    max-width: var(--page-width);

    --atlas-image_figcaption_padding: 3px var(--site-padding);

    @media only screen and (width >= 1280px) {
      --atlas-image_figcaption_padding: 3px 0px;
    }
  }

  &.wide {
    inline-size: min(100%, 100vw);
    max-width: 1600px;

    --atlas-image_figcaption_padding: 3px var(--site-padding);

    @media only screen and (width >= 1600px) {
      --atlas-image_figcaption_padding: 3px 0px;
    }

    &:not(:has(.figcaption)) {
      .credit {
        padding: 3px var(--site-padding);

        @media only screen and (width >= 1600px) {
          padding: 3px 0px;
        }
      }
    }
  }
}
</style>
